import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, ExternalLink, BookOpen, GraduationCap, Calendar } from 'lucide-react';

interface LearningResource {
  id: string;
  title: string;
  description: string;
  url: string;
  icon_type: 'book' | 'school' | 'events';
  sort_order: number;
  is_published: boolean;
  created_at: string;
}

export const LearningResourcesManager = () => {
  const [resources, setResources] = useState<LearningResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    icon_type: 'book' as 'book' | 'school' | 'events',
    sort_order: 0,
    is_published: true
  });

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const { data, error } = await supabase
        .from('learning_resources')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setResources((data as any) || []);
    } catch (error: any) {
      console.error('Error loading resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingId) {
        const { error } = await supabase
          .from('learning_resources')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
        toast.success('Resource updated successfully');
      } else {
        const { error } = await supabase
          .from('learning_resources')
          .insert([formData]);

        if (error) throw error;
        toast.success('Resource created successfully');
      }

      resetForm();
      loadResources();
    } catch (error: any) {
      console.error('Error saving resource:', error);
      toast.error('Failed to save resource');
    }
  };

  const deleteResource = async (id: string) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;

    try {
      const { error } = await supabase
        .from('learning_resources')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Resource deleted');
      loadResources();
    } catch (error: any) {
      console.error('Error deleting resource:', error);
      toast.error('Failed to delete resource');
    }
  };

  const startEdit = (resource: LearningResource) => {
    setEditingId(resource.id);
    setFormData({
      title: resource.title,
      description: resource.description,
      url: resource.url,
      icon_type: resource.icon_type,
      sort_order: resource.sort_order,
      is_published: resource.is_published
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      url: '',
      icon_type: 'book',
      sort_order: 0,
      is_published: true
    });
    setShowForm(false);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'book': return <BookOpen className="h-5 w-5" />;
      case 'school': return <GraduationCap className="h-5 w-5" />;
      case 'events': return <Calendar className="h-5 w-5" />;
      default: return <ExternalLink className="h-5 w-5" />;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Learning Resources</h2>
        <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {showForm ? 'Cancel' : 'Add Resource'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Resource' : 'New Resource'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">URL</label>
                <Input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Icon Type</label>
                <select
                  value={formData.icon_type}
                  onChange={(e) => setFormData({ ...formData, icon_type: e.target.value as any })}
                  className="w-full bg-background border border-border rounded-lg p-2"
                >
                  <option value="book">Book</option>
                  <option value="school">School</option>
                  <option value="events">Events</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Sort Order</label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm">Published</label>
              </div>
              <div className="flex gap-2">
                <Button type="submit">Save</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {resources.map((resource) => (
          <Card key={resource.id} className={!resource.is_published ? 'opacity-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-1">{getIcon(resource.icon_type)}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{resource.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline mt-2 inline-flex items-center gap-1"
                    >
                      {resource.url} <ExternalLink className="h-3 w-3" />
                    </a>
                    <div className="text-xs text-muted-foreground mt-2">
                      Order: {resource.sort_order} | {resource.is_published ? 'Published' : 'Draft'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(resource)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteResource(resource.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
